import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useGetOrderCountsQuery, useGetOrdersQuery } from "./ordersApi";
import { groupOrders } from "./groupOrders";
import { STATUS_GROUPS } from "./orderStatus";

const STALE_AFTER = 60000;

export const isStatusGroup = (value) =>
  Boolean(value) && Object.prototype.hasOwnProperty.call(STATUS_GROUPS, value);

export const useOrdersList = (initialTab, pageStep) => {
  const [statusGroup, setStatusGroup] = useState(
    isStatusGroup(initialTab) ? initialTab : STATUS_GROUPS.PENDING
  );
  const [size, setSize] = useState(pageStep);
  const [paginating, setPaginating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const lastSettledAt = useRef(0);

  const listQuery = useGetOrdersQuery({ statusGroup, size });
  const countsQuery = useGetOrderCountsQuery();

  const { data, currentData, isFetching, isError, error, refetch } = listQuery;

  const page = currentData ?? (paginating ? data : undefined);

  useEffect(() => {
    if (!isFetching) {
      setPaginating(false);
      lastSettledAt.current = Date.now();
    }
  }, [isFetching]);

  useEffect(() => {
    if (!isFetching && !countsQuery.isFetching) setRefreshing(false);
  }, [isFetching, countsQuery.isFetching]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    refetch();
    countsQuery.refetch();
  }, [refetch, countsQuery]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastSettledAt.current < STALE_AFTER) return;

      refetch();
      countsQuery.refetch();
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refetch, countsQuery]);

  const changeTab = useCallback(
    (nextTab) => {
      if (!isStatusGroup(nextTab) || nextTab === statusGroup) return;

      setSize(pageStep);
      setPaginating(false);
      setStatusGroup(nextTab);
    },
    [statusGroup, pageStep]
  );

  const loadMore = useCallback(() => {
    if (page?.isLast || isFetching || isError) return;

    setPaginating(true);
    setSize((previous) => previous + pageStep);
  }, [page, isFetching, isError, pageStep]);

  const rows = useMemo(() => groupOrders(page?.items), [page]);

  return {
    statusGroup,
    changeTab,
    rows,
    counts: countsQuery.data,
    isLast: page?.isLast ?? true,
    showSkeleton: !page && !paginating,
    paginating: isFetching && paginating,
    refreshing,
    refresh,
    loadMore,
    errorKind: isError && !page ? error?.kind || "network" : null,
  };
};
